/**
 * RiskHeatmap — interactive 5×5 likelihood × impact matrix used in Day-6/7
 * of the AI Risk Essentials workflow.
 *
 * Each registry row is plotted as a gold dot inside the cell matching its
 * (likelihood, impact) score.  Multiple risks sharing the same cell are
 * stacked using a small offset so each dot remains addressable.  Hover or
 * focus a dot to highlight the corresponding row in the registry table.
 *
 * The cell background colours follow the standard ERM convention:
 *   - Score 1-4  → green (acceptable)
 *   - Score 5-9  → yellow (monitor)
 *   - Score 10-14 → orange (action plan)
 *   - Score 15-25 → red (escalate)
 */
import React from 'react';

const CELL_BG = [
  // index = (likelihood * impact)
  null,
  '#D9F3DE', '#D9F3DE', '#D9F3DE', '#D9F3DE',                     // 1-4   green
  '#FBF1C2', '#FBF1C2', '#FBF1C2', '#FBF1C2', '#FBF1C2',          // 5-9   yellow
  '#FBD9B5', '#FBD9B5', '#FBD9B5', '#FBD9B5', '#FBD9B5',          // 10-14 orange
  '#F5B5B5', '#F5B5B5', '#F5B5B5', '#F5B5B5', '#F5B5B5',          // 15-19 red
  '#F19090', '#F19090', '#F19090', '#F19090', '#F19090', '#E76F6F',// 20-25 deep red
];

function cellColour(likelihood, impact) {
  return CELL_BG[likelihood * impact] || '#FFFFFF';
}

const SIZE = 380;       // matrix viewBox size
const PADDING = 36;     // room for axis labels
const GRID = SIZE - PADDING * 2;
const CELL = GRID / 5;

/**
 * @param {Object[]} rows         — registry rows with {risk_id, title, likelihood, impact, category}
 * @param {string}   highlightId  — risk_id whose dot should glow
 * @param {Function} onSelect     — fired with risk_id when a dot is clicked
 */
export default function RiskHeatmap({ rows = [], highlightId = null, onSelect }) {
  // Group rows by cell so we can offset stacked dots
  const cellMap = new Map();
  rows.forEach((r) => {
    const key = `${r.likelihood}-${r.impact}`;
    if (!cellMap.has(key)) cellMap.set(key, []);
    cellMap.get(key).push(r);
  });

  return (
    <svg
      viewBox={`0 0 ${SIZE} ${SIZE}`}
      className="w-full max-w-[460px] h-auto select-none"
      data-testid="erm-heatmap"
    >
      {/* Axis labels */}
      <text x={SIZE / 2} y={SIZE - 6} textAnchor="middle"
            className="fill-slate-700 font-bold uppercase"
            style={{ fontSize: 11, letterSpacing: 2 }}>
        Impact →
      </text>
      <text x={12} y={SIZE / 2} textAnchor="middle"
            transform={`rotate(-90 12 ${SIZE / 2})`}
            className="fill-slate-700 font-bold uppercase"
            style={{ fontSize: 11, letterSpacing: 2 }}>
        ← Likelihood
      </text>

      {/* Cells — likelihood from bottom to top (1..5), impact from left to right (1..5) */}
      {[1, 2, 3, 4, 5].map((likelihood) =>
        [1, 2, 3, 4, 5].map((impact) => {
          const x = PADDING + (impact - 1) * CELL;
          // SVG y grows downward — invert so likelihood 5 ends up at the top
          const y = PADDING + (5 - likelihood) * CELL;
          return (
            <g key={`${likelihood}-${impact}`}>
              <rect
                x={x} y={y} width={CELL} height={CELL}
                fill={cellColour(likelihood, impact)}
                stroke="#fff" strokeWidth={2}
              />
              <text
                x={x + 6} y={y + 14}
                className="fill-slate-600 font-bold"
                style={{ fontSize: 10, opacity: 0.6 }}
              >
                {likelihood * impact}
              </text>
            </g>
          );
        }),
      )}

      {/* Axis ticks */}
      {[1, 2, 3, 4, 5].map((n) => (
        <text
          key={`x-${n}`}
          x={PADDING + (n - 0.5) * CELL}
          y={SIZE - PADDING + 18}
          textAnchor="middle"
          className="fill-slate-500 font-bold"
          style={{ fontSize: 10 }}
        >
          {n}
        </text>
      ))}
      {[1, 2, 3, 4, 5].map((n) => (
        <text
          key={`y-${n}`}
          x={PADDING - 12}
          y={PADDING + (5 - n) * CELL + CELL / 2 + 4}
          textAnchor="middle"
          className="fill-slate-500 font-bold"
          style={{ fontSize: 10 }}
        >
          {n}
        </text>
      ))}

      {/* Dots — one per registry row */}
      {[...cellMap.entries()].map(([key, list]) => {
        const [likelihood, impact] = key.split('-').map(Number);
        const cellX = PADDING + (impact - 1) * CELL;
        const cellY = PADDING + (5 - likelihood) * CELL;
        return list.map((r, idx) => {
          // Stagger dots in a 3-column grid inside the cell
          const cols = 3;
          const col = idx % cols;
          const row = Math.floor(idx / cols);
          const dotX = cellX + CELL / 2 + (col - 1) * 12;
          const dotY = cellY + CELL / 2 + (row - 0.5) * 12;
          const isHi = r.risk_id === highlightId;
          return (
            <g
              key={r.risk_id}
              onClick={() => onSelect && onSelect(r.risk_id)}
              style={{ cursor: 'pointer' }}
              data-testid={`erm-heatmap-dot-${r.risk_id}`}
            >
              <circle
                cx={dotX} cy={dotY} r={isHi ? 9 : 6}
                fill={isHi ? '#8B3FC5' : '#0A0A0A'}
                stroke="#fff" strokeWidth={2}
              />
              <title>{r.title} · L{r.likelihood} × I{r.impact}</title>
            </g>
          );
        });
      })}
    </svg>
  );
}
